import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiPath, apiPathV4 } from '../../config';
import { cleanupAuthParams } from '../../utils/authUtils';
import { isAppLoginPath, redirectToAppLogin } from '../../utils/appLogin';
import { scheduleDeferred } from '../../utils/scheduleDeferred';
import { clearLocalStorageItems } from './Functions';
import { useAppAuth } from '../../contexts';

// Throttle window for recording the user's last_login on our DB. The Auth0 SPA
// SDK silently restores sessions and refreshes tokens, so onRedirectCallback
// (which sets `justLoggedIn`) does not fire on every visit. Without this
// throttle, returning users would never update users.last_login_date and the
// admin User list would sort/filter by stale values.
const LAST_LOGIN_THROTTLE_MS = 12 * 60 * 60 * 1000;

const ForumListContext = createContext();

export const ForumListProvider = ({ children }) => {
  const [forumIds, setForumIds] = useState(() => {
    // Load forumIds from local storage if available
    const storedForumIds = localStorage.getItem('forumIds');
    return storedForumIds ? JSON.parse(storedForumIds) : [];
  });
  const [selectedForumId, setSelectedForumId] = useState(
    localStorage.getItem('selectedForumId') || null
  );
  const [selectForumName, setSelectForumName] = useState(
    localStorage.getItem('selectForumName') || null
  );
  const [eptChannels, setEptChannels] = useState(() => {
    // Load eptChannels from local storage if available
    const storedEptChannels = localStorage.getItem('eptChannels');
    return storedEptChannels ? JSON.parse(storedEptChannels) : [];
  });

  const {
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
    isAuthenticated,
  } = useAppAuth();
  const [redirectHandled, setRedirectHandled] = useState(false);
  const [lastLoginInProgress, setLastLoginInProgress] = useState(false);

  const allowedPages = () => {
    // TrainingAuthSync stashes its external returnTo before redirecting itself.
    // Auto-login here would race that and drop ?returnTo= from the handoff.
    const pathname = window.location.pathname;
    return (
      pathname === '/welcome' ||
      pathname === '/welcome-user' ||
      pathname === '/training-auth-sync' ||
      // isAppLoginPath, not ===, so `/login/` is skipped here too. Otherwise
      // this interval keeps clearing caches while the user sits on the form.
      isAppLoginPath(pathname)
    );
  };

  // Function to check and renew token. Wrapped in useCallback so its identity is
  // stable across renders, which keeps the memoized context value below stable.
  const checkAndRenewToken = useCallback(async () => {
    // Skip redirect if on welcome page
    if (allowedPages()) {
      return;
    }

    if (!isLoading && !isAuthenticated) {
      // User is not authenticated, collect email on /login first. Only drop the
      // cached channel state once the redirect is really happening: when it is
      // suppressed (Canvas, localhost) nothing unmounts, so clearing first
      // would wipe the cache again on every 15-minute tick.
      if (redirectToAppLogin(`${window.location.pathname}${window.location.search}`)) {
        clearLocalStorageItems();
      }
      return;
    }
    if (!isLoading) {
      // console.log("Checking and renewing token");
      try {
        const checkToken = await getIdTokenClaims();
        // console.log(checkToken);
        if (checkToken) {
          const currentTime = Date.now() / 1000; // Current time in seconds

          // Check if the token has expired
          if (checkToken.exp < currentTime) {
            console.log('ID Token has expired');
            // Handle the expiration scenario (e.g., log out, renew token)
            clearLocalStorageItems();
            logout({ logoutParams: { returnTo: window.location.origin } });
          } else {
            console.log('ID Token is still valid');
          }
        }
      } catch (error) {
        // Same rule as above: only clear the cache when we are actually leaving.
        if (redirectToAppLogin(`${window.location.pathname}${window.location.search}`)) {
          clearLocalStorageItems();
        }
        console.error('Error renewing token: ', error);
        // Log the user out if token renewal fails
      }
    }
  }, [isLoading, isAuthenticated, getIdTokenClaims, logout]);

  // Set up an interval for the token check
  useEffect(() => {
    // Check immediately - catch any errors to prevent unhandled rejections
    checkAndRenewToken().catch(error => {
      console.warn('Token check failed:', error?.message || 'Unknown error');
    });

    const intervalId = setInterval(
      () => {
        checkAndRenewToken().catch(error => {
          console.warn('Token renewal check failed:', error?.message || 'Unknown error');
        });
      },
      15 * 60 * 1000
    ); // Check every 15 minutes

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [isLoading, getIdTokenClaims, logout, getAccessTokenSilently]);

  useEffect(() => {
    async function fetchData() {
      // Skip API calls if on welcome page
      if (allowedPages()) {
        return;
      }

      try {
        // Get the ID token from Auth0
        const idTokenClaims = await getIdTokenClaims();
        if (!idTokenClaims?.__raw) {
          console.log('ID token claims are undefined or null');
          return; // Early return if token is not available
        }
        const idToken = idTokenClaims.__raw;

        // console.log('idToken', idToken);

        const options = {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        };
        const path = `/forum_list`;
        const url = `${apiPath()}${path}`;
        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();

        // Handle wrapped response format from API (result.data) or direct array
        const forumData = Array.isArray(result) ? result : (result.data || []);
        const forumOptions = forumData.map(forum => ({
          value: forum.forum_id,
          label: forum.url,
        }));

        setForumIds(forumOptions);
        setSelectedForumId(forumOptions[0]?.value);
        setSelectForumName(forumOptions[0]?.label);
      } catch (error) {
        console.error('Error fetching forum list:', error);
      }
    }

    async function fetchChannels() {
      // Skip API calls if on welcome page
      if (allowedPages()) {
        return;
      }

      try {
        // Get the ID token from Auth0
        const getidToken = await getIdTokenClaims();
        if (!getidToken?.__raw) {
          console.log('ID token not available for channel fetch');
          return; // Early return if token is not available
        }
        const idToken = getidToken.__raw;
        const options = {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        };
        const path = `/ask_a_question_channels`;
        const url = `${apiPath()}${path}`;
        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        // Handle wrapped response format from API (result.data) or direct array
        const channelsData = Array.isArray(result) ? result : (result.data || []);
        // console.log("result", channelsData);
        if (channelsData.length === 0) {
          setEptChannels('access_denied');
        } else {
          setEptChannels(channelsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }

    async function recordLastLoginIfNeeded() {
      // Fire after a fresh Auth0 redirect, or whenever the previous record is
      // missing or older than LAST_LOGIN_THROTTLE_MS. The throttle survives
      // reloads (it lives in localStorage), so we don't need an in-memory
      // guard here.
      try {
        if (lastLoginInProgress) {
          return;
        }

        // Ensure authenticated before calling
        if (!isAuthenticated) {
          return;
        }

        const justLoggedIn = localStorage.getItem('justLoggedIn');
        const lastRecordedRaw = localStorage.getItem('lastLoginRecordedAt');
        const lastRecordedMs = lastRecordedRaw ? Date.parse(lastRecordedRaw) : NaN;
        const isRecentlyRecorded =
          Number.isFinite(lastRecordedMs) &&
          Date.now() - lastRecordedMs < LAST_LOGIN_THROTTLE_MS;

        if (!justLoggedIn && isRecentlyRecorded) {
          return;
        }

        const idTokenClaims = await getIdTokenClaims();
        if (!idTokenClaims) {
          return;
        }
        const idToken = idTokenClaims.__raw;

        const options = {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        };
        // v4 serves Auth0 and Cognito logins alike; v3 only knew Auth0.
        const url = `${apiPathV4()}/user/last_login`;
        setLastLoginInProgress(true);
        try {
          // The API -> ept-data-management cold-start chain can outrun API
          // Gateway's 30s integration cap, surfacing as a 503 before our handler
          // ever runs. One retry is enough: it lands on the container the
          // timed-out request just warmed. EPT-4558.
          let response = await fetch(url, options);
          if (response && response.status === 503) {
            response = await fetch(url, options);
          }
          if (response && response.ok) {
            // Mark success persistently so the throttle skips the next reload,
            // and clear the one-shot fresh-redirect flag.
            try {
              localStorage.setItem('lastLoginRecordedAt', new Date().toISOString());
              localStorage.removeItem('justLoggedIn');
            } catch (storageErr) {
              // ignore storage errors
            }
          } else {
            // Leave lastLoginRecordedAt untouched so the next session retries.
            console.warn(
              `[last_login] failed: ${response ? response.status : 'no_response'}`
            );
          }
        } catch (err) {
          console.warn('[last_login] error:', err);
        } finally {
          setLastLoginInProgress(false);
        }
      } catch (e) {
        // Swallow errors; this should not block the app
      }
    }
    const forumData = async () => {
      // Only fetch data if it's not already loaded from local storage
      if (forumIds.length === 0) {
        // Fetch forumIds from API
        fetchData().catch(() => {});
      }
      if (eptChannels.length === 0) {
        // Fetch eptChannels from API
        fetchChannels().catch(() => {});
      }
    };

    // if (!isLoading && isAuthenticated && !redirectHandled) {
    //   const redirectPath = localStorage.getItem('redirectPath');
    //   if (redirectPath !== null && redirectPath !== window.location.pathname && redirectPath !== '') {
    //     window.location.href = redirectPath;
    //     localStorage.removeItem('redirectPath');
    //   }
    //   setRedirectHandled(true);
    // }

    if (!isLoading && isAuthenticated) {
      // Clean up auth parameters only when user is fully authenticated
      cleanupAuthParams();
      // Fresh users still need forum/channels immediately; last_login is deferred.
      forumData().catch(() => {});
      return scheduleDeferred(() => {
        recordLastLoginIfNeeded().catch(() => {});
      });
    }
  }, [isLoading, getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    // Store eptChannels and forumIds to local storage whenever they change
    localStorage.setItem('eptChannels', JSON.stringify(eptChannels));
    localStorage.setItem('forumIds', JSON.stringify(forumIds));
  }, [eptChannels, forumIds]);

  useEffect(() => {
    // Store selectedForumId to local storage whenever it changes
    localStorage.setItem('selectedForumId', selectedForumId);
    localStorage.setItem('selectForumName', selectForumName);
  }, [selectedForumId, selectForumName]);

  // Memoized so consumers don't re-render on every provider render (only when a
  // real value changes). Setters from useState are already stable.
  const contextValue = useMemo(
    () => ({
      forumIds,
      selectedForumId,
      setSelectedForumId,
      selectForumName,
      setSelectForumName,
      eptChannels,
      checkAndRenewToken, // Expose the token renewal function
    }),
    [forumIds, selectedForumId, selectForumName, eptChannels, checkAndRenewToken]
  );

  return <ForumListContext.Provider value={contextValue}>{children}</ForumListContext.Provider>;
};

export const useForumList = () => {
  const context = useContext(ForumListContext);

  // Return default values if context is undefined (component outside provider)
  // This prevents "Cannot destructure property 'forumIds' of undefined" errors
  if (context === undefined) {
    return {
      forumIds: [],
      selectedForumId: null,
      setSelectedForumId: () => {},
      selectForumName: null,
      setSelectForumName: () => {},
      eptChannels: [],
      checkAndRenewToken: async () => {},
    };
  }

  return context;
};
